import React, { useState, useMemo } from 'react';
import { Briefcase } from 'lucide-react';
import { useTasks } from '../../../../context/TasksContext';
import WorkOrderSheet from './WorkOrderSheet';
import JobCard from './JobCard';

/**
 * JobsTab — Customer Profile Jobs tab.
 *
 * Canonical historical record: shows EVERY job task for this customer —
 * recurring + one-time, upcoming + completed + cancelled, chronological.
 * For the Overview at-a-glance subset of actionable one-time jobs only,
 * see `OneTimeJobsSection.jsx`. The two surfaces share the same `JobCard`
 * component so their visual language stays in lock-step.
 *
 * Click any job card → opens `WorkOrderSheet` (technician completion UI).
 */
const JobsTab = ({ client }) => {
  const { tasks, loading } = useTasks();
  const [selectedTaskId, setSelectedTaskId] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  // Determine the leadId for this client (mirrors QuotesTab logic)
  // - Converted client → use client.leadId
  // - Raw lead (no clientId) → use client.id
  const leadId = client?.leadId || (!client?.clientId ? client?.id : null);

  const jobTasks = useMemo(() => {
    if (!leadId) return [];
    return tasks.filter((t) => t.type === 'job' && t.leadId === leadId);
  }, [tasks, leadId]);

  // Derive the LIVE selected task from context so WorkOrderSheet always sees
  // updated data (e.g. status flips to 'in_progress' after Start Job).
  const selectedTask = selectedTaskId
    ? (jobTasks.find((t) => t.id === selectedTaskId) || null)
    : null;

  const openWorkOrder = (task) => {
    setSelectedTaskId(task.id);
    setSheetOpen(true);
  };

  const closeWorkOrder = () => {
    setSheetOpen(false);
    setTimeout(() => setSelectedTaskId(null), 300);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
        <span className="ml-3 text-sm text-gray-500">Loading jobs...</span>
      </div>
    );
  }

  if (!leadId) {
    return (
      <div className="text-center py-12 text-gray-400">
        <Briefcase className="w-10 h-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">No lead linked to this client</p>
      </div>
    );
  }

  if (jobTasks.length === 0) {
    return (
      <div className="text-center py-12">
        <Briefcase className="w-12 h-12 mx-auto mb-3 text-gray-300" />
        <p className="text-sm font-medium text-gray-600">No jobs yet</p>
        <p className="text-xs text-gray-400 mt-1">
          Schedule a job from the approved quote to see it here.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3" data-testid="jobs-list">
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-base font-semibold text-gray-900">
            Jobs <span className="text-sm font-normal text-gray-400">({jobTasks.length})</span>
          </h2>
        </div>

        {/* Independently scrollable list — matches the ActivityTimelineTab
            pattern so the Customer Profile no longer grows unbounded when a
            long-tenured customer accumulates many jobs. Header stays fixed
            above the scroll region; each card preserves its existing click
            → WorkOrderSheet behaviour. */}
        <div
          className="max-h-[60vh] sm:max-h-[28rem] overflow-y-auto overscroll-contain pr-2 -mr-2 space-y-3"
          data-testid="jobs-scroll-container"
        >
          {jobTasks.map((task) => (
            <JobCard key={task.id} task={task} onClick={openWorkOrder} />
          ))}
        </div>
      </div>

      <WorkOrderSheet
        task={selectedTask}
        client={client}
        open={sheetOpen}
        onClose={closeWorkOrder}
        onCompleted={closeWorkOrder}
      />
    </>
  );
};

export default JobsTab;
